// VendorAppsAdmin.js — Admin Settings → Apps (docs/vendor-apps-contract.md §5).
//
// The admin pastes ONE link — anything the vendor gave them — and we do the
// rest: `POST /v1/vendorApps/resolve` derives the origin, reads the vendor's
// manifest (`/.well-known/comprehend-app.json`: name, description, icon, launch
// URL, BAA contact) and checks that both our origins are allowed to frame the
// app. What comes back is shown as a plain card; the technical fields live
// under "Advanced" for the rare override.
//
// Turning an app on is the dangerous action — it is what lets a third party's
// page load inside Comprehend, receive the open patient's name and DOB, and get
// answers computed from the visit — so it goes through the same confirm dialog
// other admin actions use. We do not hold the vendor's BAA; the admin confirms
// their organization has one, and we stamp who confirmed.
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Alert, Badge, Button, Card, CardBody, CardHeader, Col, Form, FormGroup, Input, InputGroup, InputGroupAddon, Label, Row } from 'reactstrap';
import { GlobalContext } from '../../api/GlobalContext';
import ConfirmationModal from '../../components/Misc/ConfirmationModal';
import vendorStore from '../../utils/vendorApps/store';
import { fetchPartners } from '../../utils/vendorApps/partners';
import { webAppOrigin, webAppUrl } from '../../utils/vendorApps/webOrigin';

const WEB_ORIGIN = 'https://app.comprehendpt.com';

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^(www|app)\./, '');
  } catch (e) {
    return '';
  }
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');

// Framing verdicts come back keyed by embedder origin. "ok" = every embedder allowed.
const framingSummary = (framing) => {
  if (!framing) return { state: 'unknown' };
  if (framing.error) return { state: 'unreachable', detail: framing.error };
  const results = framing.results || {};
  const blocked = Object.entries(results).filter(([, r]) => !r.allowed);
  if (blocked.length === 0) return { state: 'ok' };
  return { state: 'blocked', web: Boolean(results[WEB_ORIGIN]?.allowed), detail: blocked.map(([, r]) => r.reason).join('; ') };
};

export default function VendorAppsAdmin() {
  const { apiClient } = useContext(GlobalContext);
  const [apps, setApps] = useState([]);
  const [isAdmin, setIsAdmin] = useState(true);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);

  // Add flow: link → resolved card → confirm. Edit flow reuses the card with `editing` set.
  const [link, setLink] = useState('');
  const [resolving, setResolving] = useState(false);
  const [card, setCard] = useState(null); // { id?, name, launchUrl, embedOrigin, description, icon, color, docs, baaContact, manifestUrl, framing, manifestNote, enabled }
  const [manifestUrl, setManifestUrl] = useState(''); // Advanced: a manifest that is not at /.well-known
  const [baaAcknowledged, setBaa] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: 'enable' | 'delete', payload }
  const linkInput = useRef(null);

  useEffect(() => {
    let alive = true;
    // Inside the Chrome extension our own origin has no partners.json; fetch it
    // from the web app that matches the backend we point at.
    fetchPartners(undefined, webAppOrigin(apiClient)).then((list) => alive && setPartners(list));
    return () => {
      alive = false;
    };
  }, [apiClient]);

  // The profile omits `vendor_apps` when the backend has no Vendor Apps routes
  // (flag on, backend not deployed — e.g. prod before release). Say so calmly
  // instead of failing the list call.
  const backendMissing = apiClient?.vendorAppsSupported === false;
  const load = useCallback(async () => {
    if (backendMissing) {
      setApps([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const rsp = await apiClient.listVendorApps();
      if (rsp?.error) throw new Error(rsp.error);
      setApps(rsp.vendor_apps || []);
      setIsAdmin(rsp.isAdmin !== false);
    } catch (e) {
      toast.error(`Could not load apps: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [apiClient, backendMissing]);

  useEffect(() => {
    // Before the profile loads the client may still point at the default backend
    // (the dev/prod switch happens when /v1/user/stats returns); wait for it.
    if (apiClient?.vendorAppsSupported === undefined && typeof apiClient?.onProfileUpdated === 'function') {
      const once = () => {
        apiClient.offProfileUpdated?.(once);
        load();
      };
      apiClient.onProfileUpdated(once);
      return () => apiClient.offProfileUpdated?.(once);
    }
    load();
    return undefined;
  }, [apiClient, load]);

  const reset = () => {
    setCard(null);
    setBaa(false);
    setShowAdvanced(false);
    setLink('');
    setManifestUrl('');
  };

  // The one technical step, hidden behind "Look it up".
  const resolve = useCallback(
    async (rawUrl, explicitManifestUrl = null, keep = null) => {
      const url = String(rawUrl || '').trim();
      if (!url) return;
      if (!/^https:\/\//i.test(url)) {
        toast.error('That link needs to start with https:// — ask your vendor for the secure link to their app.');
        return;
      }
      setResolving(true);
      if (!keep) setCard(null);
      try {
        const rsp = await apiClient.resolveVendorApp(url, explicitManifestUrl || null);
        if (rsp?.error) throw new Error(rsp.error);
        const m = rsp.manifest || {};
        setManifestUrl(rsp.manifestUrl || '');
        setCard({
          id: keep?.id || null,
          name: rsp.name || hostOf(url),
          launchUrl: rsp.launchUrl || url,
          adminUrl: m.adminUrl || null,
          embedOrigin: rsp.origin,
          description: m.description || '',
          icon: m.icon || '',
          color: m.color || '',
          docs: m.docs || '',
          baaContact: m.baaContact || '',
          hasManifest: Boolean(rsp.manifest),
          manifestNote: rsp.manifestNote,
          framing: rsp.framing,
          enabled: keep?.enabled || false,
        });
        if (!keep) setBaa(false);
      } catch (e) {
        toast.error(`We couldn't look that link up: ${e.message}`);
      } finally {
        setResolving(false);
      }
    },
    [apiClient],
  );

  // "Add to Comprehend" deep link: /User/admin/settings#vendorApps?add=<launch URL>
  // (routed here by views/VendorApps.js). We resolve it straight away; the admin
  // still confirms the BAA and the enable dialog before anything goes live.
  useEffect(() => {
    const hash = window.location.hash || '';
    const q = hash.indexOf('?');
    if (q === -1) return;
    const params = new URLSearchParams(hash.slice(q + 1));
    const add = params.get('add');
    if (!add) return;
    // When the vendor's link carries a manifest, that is the richer input: put it
    // in the box (a .json link is read as the manifest) so "Look it up" keeps working.
    const manifest = params.get('manifest');
    setLink(manifest || add);
    if (manifest) setManifestUrl(manifest);
    resolve(manifest || add);
  }, [resolve]);

  // Keep the Apps tab (and any mounted frames) in step with what admins change.
  const syncStore = (list) => {
    apiClient.vendorApps = (list || []).filter((a) => a.enabled);
    vendorStore.loadApps(apiClient.vendorApps);
  };

  const save = async (payload) => {
    setSaving(true);
    try {
      const rsp = await apiClient.upsertVendorApp(payload);
      if (rsp?.error) throw new Error(rsp.error);
      setApps(rsp.vendor_apps || []);
      syncStore(rsp.vendor_apps);
      reset();
      toast.success(payload.enabled ? `${payload.name} is on for your clinic` : `${payload.name} added — it stays off until you turn it on`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const payloadFrom = (c, enabled) => ({
    id: c.id || undefined,
    name: c.name.trim(),
    embedOrigin: c.embedOrigin.trim().replace(/\/+$/, ''),
    launchUrl: c.launchUrl.trim(),
    ...(c.adminUrl ? { adminUrl: c.adminUrl } : {}),
    enabled,
    baaAcknowledged: baaAcknowledged === true,
    // Presentation, from the manifest (or typed under Advanced). Shown on the Apps tab.
    description: (c.description || '').trim() || null,
    icon: (c.icon || '').trim() || null,
    color: /^#[0-9a-f]{6}$/i.test(c.color || '') ? c.color.toLowerCase() : null,
  });

  const submit = (enabled) => {
    if (!card?.name.trim()) {
      toast.error('Give the app a name.');
      return;
    }
    const payload = payloadFrom(card, enabled);
    const wasEnabled = apps.find((a) => a.id === payload.id)?.enabled;
    if (enabled && !wasEnabled) {
      if (!baaAcknowledged) {
        toast.error('Confirm that your organization has a signed BAA with this vendor before turning it on.');
        return;
      }
      setConfirm({ kind: 'enable', payload });
      return;
    }
    save(payload);
  };

  const remove = async (appId) => {
    try {
      const rsp = await apiClient.deleteVendorApp(appId);
      if (rsp?.error) throw new Error(rsp.error);
      setApps(rsp.vendor_apps || []);
      syncStore(rsp.vendor_apps);
      toast.success('App removed');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const edit = (app) => {
    setCard({
      id: app.id,
      name: app.name || '',
      launchUrl: app.launchUrl || '',
      adminUrl: app.adminUrl || null,
      embedOrigin: app.embedOrigin || '',
      description: app.description || '',
      icon: app.icon || '',
      color: app.color || '',
      docs: '',
      baaContact: '',
      hasManifest: null,
      framing: null,
      enabled: Boolean(app.enabled),
    });
    setManifestUrl('');
    setBaa(Boolean(app.baa));
    setShowAdvanced(true);
    setLink(app.launchUrl || '');
  };

  const turnOff = (app) =>
    save({
      id: app.id,
      name: app.name,
      embedOrigin: app.embedOrigin,
      launchUrl: app.launchUrl,
      adminUrl: app.adminUrl || null,
      enabled: false,
      baaAcknowledged: false,
      description: app.description || null,
      icon: app.icon || null,
      color: app.color || null,
    });

  const setField = (k) => (e) => setCard((c) => ({ ...c, [k]: e.target.value }));

  if (!isAdmin) {
    return <p className="text-muted">Only group admins and coordinators can manage apps.</p>;
  }

  const framing = card ? framingSummary(card.framing) : null;
  const canTurnOn = card && baaAcknowledged && framing.state !== 'blocked';
  const vendorHost = card ? hostOf(card.embedOrigin) : '';

  return (
    <Row>
      {backendMissing && (
        <Col xs="12">
          <Alert color="secondary" className="mb-3">
            Vendor Apps aren’t available on this server yet. Nothing here will load until the backend release that adds them.
          </Alert>
        </Col>
      )}
      <Col xs="12">
        <Alert color="warning" className="mb-3">
          <strong>Turning an app on shares your patients’ visit information with that vendor.</strong> When a clinician opens it, the vendor’s app receives the open
          patient’s name and date of birth, and answers computed from what was said in the visit. Comprehend does not hold or verify your agreement with the vendor —{' '}
          <strong>you must have a signed Business Associate Agreement with any vendor you turn on here.</strong>
        </Alert>
      </Col>

      {/* ---------------------------------------------------------------- Registered */}
      <Col xs="12" className="mb-3">
        <Card>
          <CardHeader className="py-2 d-flex align-items-center justify-content-between">
            <strong>Your apps</strong>
            <small className="text-muted">Apps that are on appear in every clinician’s Apps tab</small>
          </CardHeader>
          <CardBody className="p-0">
            {loading ? (
              <p className="text-muted p-3 mb-0">Loading…</p>
            ) : apps.length === 0 ? (
              <p className="text-muted p-3 mb-0">No apps yet. Paste a link from your vendor to add one.</p>
            ) : (
              // Stacked rows, not a table: the Chrome side panel is ~350 px wide and a
              // four-column table squeezed there wraps the app name one letter per line.
              <div>
                {apps.map((app) => (
                  <div key={app.id} className="px-3 py-2 border-top" data-vendor-app-row={app.id}>
                    <div className="d-flex align-items-center" style={{ gap: 10, minWidth: 0 }}>
                      <span
                        className="rounded flex-shrink-0 d-flex align-items-center justify-content-center"
                        style={{ width: 36, height: 36, background: app.icon ? '#fff' : app.color || '#e9ecef', color: '#fff', fontWeight: 700, overflow: 'hidden', border: app.icon ? '1px solid #e9ecef' : 0 }}
                      >
                        {app.icon ? <img src={app.icon} alt="" width="36" height="36" style={{ objectFit: 'cover', display: 'block' }} /> : (app.name || '?').slice(0, 1).toUpperCase()}
                      </span>
                      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <div className="d-flex align-items-center" style={{ gap: 8, minWidth: 0 }}>
                          <strong className="text-truncate" style={{ minWidth: 0 }}>{app.name}</strong>
                          {app.enabled ? <Badge color="success">On</Badge> : <Badge color="secondary">Off</Badge>}
                        </div>
                        <small className="text-muted d-block text-truncate">{hostOf(app.embedOrigin) || app.embedOrigin}</small>
                      </div>
                    </div>
                    <div className="d-flex flex-wrap align-items-center small mt-1" style={{ gap: '2px 12px', paddingLeft: 46 }}>
                      {app.baa ? (
                        <span className="text-muted text-truncate" style={{ maxWidth: '100%' }} title={`Confirmed by ${app.baa.acknowledgedBy}`}>
                          BAA confirmed {fmtDate(app.baa.acknowledgedAt)} · {app.baa.acknowledgedBy}
                        </span>
                      ) : (
                        <span className="text-warning">BAA not confirmed</span>
                      )}
                    </div>
                    <div className="d-flex flex-wrap" style={{ paddingLeft: 38 }}>
                      <Button size="sm" color="link" onClick={() => edit(app)}>
                        {app.enabled ? 'Edit' : 'Turn on'}
                      </Button>
                      {app.enabled && (
                        <Button size="sm" color="link" className="text-warning" onClick={() => turnOff(app)}>
                          Turn off
                        </Button>
                      )}
                      <Button size="sm" color="link" className="text-danger" onClick={() => setConfirm({ kind: 'delete', payload: app })}>
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
        <p className="small text-muted mt-2 mb-0">
          Vendors who want to be here:{' '}
          <a href={webAppUrl(apiClient, '/partners.html')} target="_blank" rel="noreferrer">
            app.comprehendpt.com/partners
          </a>
        </p>
      </Col>
      <Col xs="12" className="mb-3">
        <Card>
          <CardHeader className="py-2 d-flex align-items-center justify-content-between">
            <strong>{card?.id ? `Edit ${card.name}` : 'Add an app'}</strong>
            {card && (
              <Button size="sm" color="link" className="p-0" onClick={reset}>
                {card.id ? 'Cancel' : 'Start over'}
              </Button>
            )}
          </CardHeader>
          <CardBody>
            {!card && (
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  resolve(link);
                }}
              >
                <FormGroup className="mb-2">
                  <Label className="mb-1">Paste the link your vendor gave you</Label>
                  <InputGroup>
                    <Input innerRef={linkInput} type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" required disabled={resolving} />
                    <InputGroupAddon addonType="append">
                      <Button color="primary" type="submit" disabled={resolving || !link.trim()}>
                        {resolving ? 'Looking it up…' : 'Look it up'}
                      </Button>
                    </InputGroupAddon>
                  </InputGroup>
                </FormGroup>
                <small className="text-muted">
                  Any link to their app works. We’ll find the app’s name, details and check it can run inside Comprehend. If they gave you a link to their{' '}
                  <em>manifest</em> (ends in .json), paste that.
                </small>
              </Form>
            )}

            {card && (
              <>
                {/* The app, as the vendor describes it */}
                <div className="d-flex align-items-start mb-3">
                  {card.icon ? (
                    <img src={card.icon} alt="" width="40" height="40" className="rounded mr-3 flex-shrink-0" />
                  ) : (
                    <div
                      className={`rounded mr-3 flex-shrink-0 d-flex align-items-center justify-content-center ${card.color ? 'text-white' : 'bg-light text-muted'}`}
                      style={{ width: 40, height: 40, fontSize: 18, fontWeight: 600, background: card.color || undefined }}
                    >
                      {(card.name || '?').slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center">
                      <strong className="mr-2">{card.name || vendorHost}</strong>
                      <small className="text-muted text-truncate">{vendorHost}</small>
                    </div>
                    {card.description ? (
                      <div className="small mt-1">{card.description}</div>
                    ) : card.hasManifest === false ? (
                      <div className="small text-muted mt-1">This vendor hasn’t published details for their app, so we’re going by its web address. You can rename it under Advanced.</div>
                    ) : null}
                    {card.docs && (
                      <a className="small d-inline-block mt-1" href={card.docs} target="_blank" rel="noreferrer">
                        Vendor’s Comprehend page
                      </a>
                    )}
                  </div>
                </div>

                {/* Plain-language checks */}
                {card.framing !== null && (
                  <ul className="list-unstyled small mb-3">
                    {framing.state === 'ok' && (
                      <li className="text-success mb-1">
                        <span aria-hidden="true">✓</span> Can run inside Comprehend — on the web and in the Chrome extension.
                      </li>
                    )}
                    {framing.state === 'blocked' && (
                      <li className="text-danger mb-1">
                        <span aria-hidden="true">✗</span> <strong>Can’t be shown inside Comprehend yet{framing.web ? ' in the Chrome extension' : ''}.</strong> Ask{' '}
                        {vendorHost} to allow Comprehend to embed their app — their developers will know what to do, and{' '}
                        <a href={webAppUrl(apiClient, '/partners.html#start')} target="_blank" rel="noreferrer">
                          this page
                        </a>{' '}
                        tells them exactly. You can add the app now and turn it on once they have.
                      </li>
                    )}
                    {framing.state === 'unreachable' && (
                      <li className="text-warning mb-1">
                        <span aria-hidden="true">!</span> We couldn’t reach {vendorHost} just now to check it. You can still add it.
                      </li>
                    )}
                    <li className={`mb-1 ${card.baaContact ? '' : 'text-muted'}`}>
                      <span aria-hidden="true">{card.baaContact ? '✓' : '·'}</span> Business Associate Agreement:{' '}
                      {card.baaContact ? (
                        /^https?:\/\//.test(card.baaContact) ? (
                          <a href={card.baaContact} target="_blank" rel="noreferrer">
                            {card.baaContact}
                          </a>
                        ) : /@/.test(card.baaContact) ? (
                          <a href={`mailto:${card.baaContact}`}>{card.baaContact}</a>
                        ) : (
                          card.baaContact
                        )
                      ) : (
                        <>ask {vendorHost} if your organization doesn’t have one yet.</>
                      )}
                    </li>
                  </ul>
                )}

                {/* Advanced: the fields the vendor normally supplies */}
                <details className="mb-3" open={showAdvanced} onToggle={(e) => setShowAdvanced(e.target.open)}>
                  <summary className="small text-muted" style={{ cursor: 'pointer' }}>
                    Advanced
                  </summary>
                  <FormGroup className="mt-2 mb-2">
                    <Label className="small mb-1">Name</Label>
                    <Input bsSize="sm" value={card.name} onChange={setField('name')} maxLength={80} />
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <Label className="small mb-1">Launch URL</Label>
                    <Input bsSize="sm" type="url" value={card.launchUrl} onChange={setField('launchUrl')} />
                    <small className="text-muted">
                      The page we open in the Apps tab. We add <code>?comprehend=1</code>.
                    </small>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <Label className="small mb-1">Origin</Label>
                    <Input bsSize="sm" value={card.embedOrigin} onChange={setField('embedOrigin')} />
                    <small className="text-muted">The app’s identity. Only messages from this exact origin are accepted; the launch URL must live on it.</small>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <Label className="small mb-1">Manifest URL</Label>
                    <InputGroup size="sm">
                      <Input value={manifestUrl} onChange={(e) => setManifestUrl(e.target.value)} placeholder={`${card.embedOrigin}/.well-known/comprehend-app.json`} />
                      <InputGroupAddon addonType="append">
                        <Button color="secondary" outline disabled={resolving || !card.launchUrl} onClick={() => resolve(card.launchUrl, manifestUrl.trim() || null, card)}>
                          {resolving ? 'Checking…' : 'Re-check'}
                        </Button>
                      </InputGroupAddon>
                    </InputGroup>
                    <small className="text-muted">
                      Where the vendor describes their app (name, icon, colour, description). Normally at <code>/.well-known/comprehend-app.json</code> on their origin; point elsewhere if they told you to.
                    </small>
                  </FormGroup>
                  <Row form>
                    <Col xs="8">
                      <FormGroup className="mb-2">
                        <Label className="small mb-1">Icon URL</Label>
                        <Input bsSize="sm" type="url" value={card.icon} onChange={setField('icon')} placeholder="https://…/icon.png" />
                      </FormGroup>
                    </Col>
                    <Col xs="4">
                      <FormGroup className="mb-2">
                        <Label className="small mb-1">Colour</Label>
                        <Input bsSize="sm" value={card.color} onChange={setField('color')} placeholder="#0f766e" maxLength={7} />
                      </FormGroup>
                    </Col>
                  </Row>
                  <FormGroup className="mb-0">
                    <Label className="small mb-1">Description</Label>
                    <Input bsSize="sm" value={card.description} onChange={setField('description')} maxLength={200} placeholder="One sentence clinicians will see" />
                  </FormGroup>
                </details>

                <hr className="my-3" />
                <FormGroup check className="mb-3">
                  <Label check className="small">
                    <Input type="checkbox" checked={baaAcknowledged} onChange={(e) => setBaa(e.target.checked)} />{' '}
                    <strong>Our organization has a signed BAA with {card.name || vendorHost}.</strong> I understand Comprehend will share our patients’ names, dates of
                    birth, and information from their visits with this vendor while the app is on.
                  </Label>
                </FormGroup>

                <div className="d-flex align-items-center">
                  {card.id ? (
                    <>
                      <Button size="sm" color="primary" onClick={() => submit(card.enabled)} disabled={saving}>
                        Save
                      </Button>
                      {!card.enabled && (
                        <Button size="sm" color="link" onClick={() => submit(true)} disabled={saving || !canTurnOn}>
                          Save and turn on
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Button size="sm" color="primary" onClick={() => submit(true)} disabled={saving || !canTurnOn} title={!baaAcknowledged ? 'Confirm the BAA first' : ''}>
                        Add and turn on
                      </Button>
                      <Button size="sm" color="link" onClick={() => submit(false)} disabled={saving}>
                        Add, keep off for now
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </CardBody>
        </Card>

        {partners.length > 0 && !card && (
          <Card className="mt-3">
            <CardHeader className="py-2 d-flex align-items-center justify-content-between">
              <strong>Partner apps</strong>
              <small className="text-muted">Reviewed by Comprehend. Not a substitute for your BAA.</small>
            </CardHeader>
            <CardBody className="py-2">
              {partners.map((p) => {
                const registered = apps.find((a) => (a.launchUrl || '').replace(/\/+$/, '').toLowerCase() === (p.launchUrl || '').replace(/\/+$/, '').toLowerCase());
                return (
                  <div key={p.id} className="d-flex align-items-center py-2 border-bottom">
                    {p.logo && <img src={p.logo} alt="" width="24" height="24" className="mr-2 rounded" />}
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <strong>{p.name}</strong> <small className="text-muted">{p.blurb}</small>
                    </div>
                    {registered ? (
                      <Badge color={registered.enabled ? 'success' : 'secondary'}>{registered.enabled ? 'On' : 'Added'}</Badge>
                    ) : (
                      <Button
                        size="sm"
                        color="primary"
                        outline
                        onClick={() => {
                          setLink(p.launchUrl);
                          resolve(p.launchUrl);
                        }}
                      >
                        Add
                      </Button>
                    )}
                  </div>
                );
              })}
            </CardBody>
          </Card>
        )}
      </Col>

      <ConfirmationModal
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        confirmButtonText={confirm?.kind === 'enable' ? 'Yes, turn on and share' : 'Remove'}
        onConfirm={() => {
          const c = confirm;
          setConfirm(null);
          if (!c) return;
          if (c.kind === 'enable') save(c.payload);
          else remove(c.payload.id);
        }}
      >
        {confirm?.kind === 'enable' ? (
          <>
            <h4>Share visit information with {confirm.payload.name}?</h4>
            <p className="mb-2">
              Every clinician in your group will see this app. When they open it, <strong>{hostOf(confirm.payload.embedOrigin) || confirm.payload.embedOrigin}</strong>{' '}
              receives the open patient’s name and date of birth, gets answers computed from what was said in the visit, and anything it provides becomes context for
              that patient’s note.
            </p>
            <p className="mb-0">
              <strong>You are confirming that your organization has a signed BAA with this vendor.</strong> Comprehend does not verify it. Your name and the time are
              recorded with this confirmation.
            </p>
          </>
        ) : (
          <>
            <h4>Remove {confirm?.payload?.name}?</h4>
            <p className="mb-0">Open frames close immediately. Patient links this app created are kept in case you add it again.</p>
          </>
        )}
      </ConfirmationModal>
    </Row>
  );
}
